import React, { useEffect, useRef } from 'react';
import { Box, FormControl, InputLabel, Select, MenuItem, Typography, TextField, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

const formatDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const SalesActionStatFilter = ({
  dptUuid = '',
  setDptUuid,
  usrUuid = '',
  setUsrUuid,
  startYear,
  setStartYear,
  startMonth,
  setStartMonth,
  endYear,
  setEndYear,
  endMonth,
  setEndMonth,
  dptList = [],
  usrList = [],
  onFetchStat
}) => {
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  // 1. [핵심 추가] dptList를 받아왔을 때, 선택된 dptUuid가 없다면 첫 번째 요소를 기본값으로 지정
  useEffect(() => {
    if (dptList && dptList.length > 0 && !dptUuid) {
      setDptUuid(dptList[0].dptUuid);
    }
  }, [dptList, dptUuid, setDptUuid]);

  const filteredUsrList = React.useMemo(() => {
    if (!dptUuid) return usrList;
    return usrList.filter(usr => usr.dptUuid === dptUuid);
  }, [dptUuid, usrList]);

  const handleStartYearChange = (newYear) => {
    setStartYear(newYear);
    if (newYear > endYear || (newYear === endYear && startMonth > endMonth)) {
      setEndYear(newYear);
      setEndMonth(startMonth);
    }
  };

  const handleStartMonthChange = (newMonth) => {
    setStartMonth(newMonth);
    if (startYear === endYear && newMonth > endMonth) {
      setEndMonth(newMonth);
    }
  };

  const handleEndYearChange = (newYear) => {
    setEndYear(newYear);
    if (newYear < startYear || (newYear === startYear && endMonth < startMonth)) {
      setStartYear(newYear);
      setStartMonth(endMonth);
    }
  };

  const handleEndMonthChange = (newMonth) => {
    setEndMonth(newMonth);
    if (startYear === endYear && newMonth < startMonth) {
      setStartMonth(newMonth);
    }
  };

  const localUser = localStorage.getItem('user');
  let loginUsrUuid = '';
  if (localUser) {
      const parsedUser = JSON.parse(localUser);
      loginUsrUuid = parsedUser.usrUuid || parsedUser.userUuid || '';
  }

  const isFirstRender = useRef(true);

useEffect(() => {
    if (!startYear || !endYear) return;

    const startStr = `${startYear}-${String(startMonth).padStart(2, '0')}-01`;
    const endStr = `${endYear}-${String(endMonth).padStart(2, '0')}-${String(new Date(endYear, endMonth, 0).getDate()).padStart(2, '0')}`;

    // 핵심: 첫 진입이든, 사용자가 도중에 '전체'로 바꿨든 상관없이 둘 다 빈 값이면 loginUsrUuid를 보냄
    const loginUuidParam = (!dptUuid && !usrUuid) ? loginUsrUuid : '';

    if (isFirstRender.current) {
        onFetchStat(startStr, endStr, dptUuid, usrUuid, loginUuidParam);
        if (dptList.length > 0) {
            isFirstRender.current = false;
        }
    } else {
        // 중도 변경 시점에도 실시간으로 판별된 loginUuidParam이 전달됨
        onFetchStat(startStr, endStr, dptUuid, usrUuid, loginUuidParam);
    }
    
}, [startYear, startMonth, endYear, endMonth, dptUuid, usrUuid, onFetchStat, dptList, loginUsrUuid]);


  const handleYearChange = (setter) => (e) => {
    const val = e.target.value.replace(/[^0-9]/g, '');
    setter(val ? Number(val) : '');
  };

  const handleYearBlur = (setter, fallbackYear) => () => {
    setter(prev => (prev && prev >= 1900) ? prev : fallbackYear);
  };

  const renderYearStepper = (yearVal, onYearChange, fallbackYear) => (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        border: '1px solid #ccc', 
        borderRadius: '4px', 
        height: '40px', 
        px: 0.5, 
        backgroundColor: '#ffffff',
        '&:hover': { borderColor: '#333333' }
      }}
    >
      <IconButton size="small" onClick={() => onYearChange(Math.max(1900, Number(yearVal) - 1))} sx={{ p: '4px' }}>
        <RemoveIcon fontSize="small" />
      </IconButton>
      <TextField
        variant="standard"
        value={yearVal}
        onChange={handleYearChange(onYearChange)}
        onBlur={handleYearBlur(onYearChange, fallbackYear)}
        inputProps={{ style: { textAlign: 'center', width: '45px', fontSize: '14px', fontWeight: '500', padding: 0 } }}
        InputProps={{ disableUnderline: true }}
      />
      <Typography variant="body2" sx={{ pr: 0.5, color: 'text.secondary', userSelect: 'none', fontSize: '14px' }}>년</Typography>
      <IconButton size="small" onClick={() => onYearChange(Number(yearVal) + 1)} sx={{ p: '4px' }}>
        <AddIcon fontSize="small" />
      </IconButton>
    </Box>
  );

  return (
    <Box 
      sx={{ 
        p: 2, 
        mb: 1.5, 
        backgroundColor: '#ffffff', 
        borderRadius: '12px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)', 
        border: '1px solid rgba(0, 0, 0, 0.03)',
        display: 'flex', 
        gap: 1.5, 
        alignItems: 'center', 
        flexWrap: 'wrap'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {renderYearStepper(startYear, handleStartYearChange, new Date().getFullYear())}
        <FormControl size="small" sx={{ width: 85 }}>
          <Select value={startMonth} onChange={(e) => handleStartMonthChange(e.target.value)}>
            {months.map(m => <MenuItem key={m} value={m}>{m}월</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      <Typography variant="body2" sx={{ color: 'text.disabled', fontWeight: '700', px: 0.2 }}>~</Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {renderYearStepper(endYear, handleEndYearChange, new Date().getFullYear())}
        <FormControl size="small" sx={{ width: 85 }}>
          <Select value={endMonth} onChange={(e) => handleEndMonthChange(e.target.value)}>
            {months.map(m => <MenuItem key={m} value={m}>{m}월</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ width: '1px', height: '24px', backgroundColor: '#e0e0e0', mx: 0.5 }} />

      <FormControl size="small" sx={{ minWidth: 120 }}>
        <InputLabel shrink id="dpt-select-label">부서</InputLabel>
        <Select 
          labelId="dpt-select-label"
          value={dptUuid} 
          label="부서" 
          displayEmpty
          notched={true}
          onChange={(e) => { setDptUuid(e.target.value); setUsrUuid(''); }}
        >
          {/* 이전 단계 백엔드 수정으로 roleCheck > 0 일 때 맨 위에 ""(전체 부서) 맵이 담겨서 오거나, 일반 리스트가 바인딩됩니다 */}
          {dptList.map(dpt => <MenuItem key={dpt.dptUuid} value={dpt.dptUuid}>{dpt.dptNm}</MenuItem>)}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 120 }}>
        <InputLabel shrink id="usr-select-label">담당자</InputLabel>
        <Select 
          labelId="usr-select-label"
          value={usrUuid} 
          label="담당자" 
          displayEmpty
          notched={true}
          onChange={(e) => setUsrUuid(e.target.value)}
        >
          <MenuItem value="">전체 담당자</MenuItem>
          {filteredUsrList.map(usr => <MenuItem key={usr.usrUuid} value={usr.usrUuid}>{usr.usrNm}</MenuItem>)}
        </Select>
      </FormControl>
    </Box>
  );
};

export default SalesActionStatFilter;
